/**
 * Design Tokens — TypeScript accessors
 *
 * For computed styles, SVG fill/stroke, and any place where
 * CSS custom properties can't be used directly.
 *
 * Source of truth: tokens.css
 * Keep this file in sync when adding/changing token values.
 */

/* ── Grade colors ──────────────────────────────────────────── */

/**
 * CSS variable name for a letter grade. Use with `var()` in
 * inline styles or read at runtime via getComputedStyle.
 *
 * Example:
 *   style={{ color: `var(${GRADE_VAR[grade]})` }}
 */
export const GRADE_VAR: Record<string, string> = {
  A: "--color-grade-a",
  B: "--color-grade-b",
  C: "--color-grade-c",
  D: "--color-grade-d",
  F: "--color-grade-f",
};

/**
 * Tailwind class for grade text color.
 *
 * Example:
 *   <span className={GRADE_CLASS[grade]}>A</span>
 */
export const GRADE_CLASS: Record<string, string> = {
  A: "text-grade-a",
  B: "text-grade-b",
  C: "text-grade-c",
  D: "text-grade-d",
  F: "text-grade-f",
};

/* ── Sentiment ─────────────────────────────────────────────── */

export const SENTIMENT_CLASS: Record<string, string> = {
  positive: "text-semantic-success",
  neutral: "text-semantic-info",
  negative: "text-semantic-error",
  mixed: "text-semantic-warning",
};

export const SENTIMENT_BORDER_CLASS: Record<string, string> = {
  positive: "border-l-semantic-success",
  neutral: "border-l-semantic-info",
  negative: "border-l-semantic-error",
  mixed: "border-l-brand-primary",
};

/* ── Runtime token reader ──────────────────────────────────── */

/**
 * Read a CSS custom property value from the document root.
 * Returns the trimmed string value or fallback.
 *
 * Example:
 *   const amber = getToken("--color-primary");
 */
export function getToken(name: string, fallback = ""): string {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

/* ── Layout constants ──────────────────────────────────────── */

export const CONTAINER = {
  xs: "max-w-[28rem]",    // 448px — auth forms
  sm: "max-w-[44rem]",    // 704px — list pages
  md: "max-w-[52rem]",    // 832px — medium content
  lg: "max-w-[76rem]",    // 1216px — report pages
  xl: "max-w-[84rem]",    // 1344px — landing page
} as const;

export const HEADER_HEIGHT = "4rem"; // 64px
